'use client';

import React, { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { motion } from 'framer-motion';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { StatsFiltersProvider, useStatsFilters } from './context/StatsFiltersContext';
import { FilterBar } from './components/filters/FilterBar';
import { RevenueMetricsV4 } from './components/RevenueMetricsV4';
import { SubscriberMetricsV4 } from './components/SubscriberMetricsV4';
import { ApprovalRatesMetricsV4 } from './components/ApprovalRatesMetricsV4';
import { UpsellTakeRateMetrics } from './components/UpsellTakeRateMetrics';
import { LoadingOverlay } from './components/LoadingOverlay';
import { useAnalyticsFast } from './hooks/useAnalyticsFast';
import { SubscriptionCalendar } from './components/SubscriptionCalendar';
import { SubscriptionDetailView } from './components/SubscriptionDetailView';
import { CustomerConversionFunnel } from './components/CustomerConversionFunnel';
import { CheckoutToOrderPaidTime } from './components/CheckoutToOrderPaidTime';
import { CheckoutToEmailEnteredTime } from './components/CheckoutToEmailEnteredTime';
import { CheckoutToBillingAddressTime } from './components/CheckoutToBillingAddressTime';
import { ConversionOverTime } from './components/ConversionOverTime';
import { ProductMetricsV4 } from './components/ProductMetricsV4';

const MetricsContainer = ({ children }: { children: React.ReactNode }) => (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="mt-6">
        {children}
    </motion.div>
);

function AnalyticsV4Content() {
    const navigate = useNavigate();
    const searchParams = useSearchParams()[0];
    const { t } = useTranslation('analytics');
    const { selectedDates, selectedGranularity, selectedStores, selectedCurrency } = useStatsFilters();

    const [activeTab, setActiveTab] = useState<string>('overview');
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam && ['overview', 'subscription', 'approval-rate', 'conversion', 'calendar', 'upsell-take-rate', 'product'].includes(tabParam)) {
            setActiveTab(tabParam);
        }
    }, [searchParams]);

    const handleTabChange = (value: string) => {
        setActiveTab(value);
        const params = new URLSearchParams(searchParams.toString());
        params.set('tab', value);
        navigate({ search: params.toString() }, { replace: true });
    };

    const granularity = selectedGranularity as 'daily' | 'weekly' | 'monthly';

    const { data: analyticsData, isLoading, loadingStates, error } = useAnalyticsFast({
        startDate: selectedDates.from,
        endDate: selectedDates.to,
        storeIds: selectedStores,
        currency: selectedCurrency,
        enabled: !!(selectedDates.from && selectedDates.to),
    });

    return (
        <section className="mx-auto max-w-[1920px] pb-12 relative">
            <div className="mb-4 flex items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                    <motion.h1 initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="scroll-mt-8 text-xl font-semibold text-foreground sm:text-2xl">{t('page.title')}</motion.h1>
                    <Badge variant="default">{t('page.beta')}</Badge>
                </div>
            </div>

            <div className="sticky -top-4 z-20 -mx-4 flex items-center justify-between border-b border-border bg-background px-4 pb-3 pt-7 sm:mx-0 sm:px-0 sm:pb-3 sm:pt-7 lg:-top-6 lg:pb-4 lg:pt-10">
                <FilterBar />
            </div>

            <div className="relative mt-6">
                <Tabs value={activeTab} onValueChange={handleTabChange}>
                    <TabsList>
                        <TabsTrigger value="overview">{t('page.tabs.overview')}</TabsTrigger>
                        <TabsTrigger value="subscription">{t('page.tabs.subscription')}</TabsTrigger>
                        <TabsTrigger value="calendar">{t('page.tabs.subscriptionCalendar')}</TabsTrigger>
                        <TabsTrigger value="approval-rate">{t('page.tabs.approvalRate')}</TabsTrigger>
                        <TabsTrigger value="upsell-take-rate">{t('page.tabs.upsellTakeRate')}</TabsTrigger>
                        <TabsTrigger value="conversion">{t('page.tabs.conversion')}</TabsTrigger>
                        <TabsTrigger value="product">{t('page.tabs.product')}</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview">
                        <MetricsContainer>
                            <RevenueMetricsV4
                                data={analyticsData as any}
                                granularity={granularity}
                                currency={selectedCurrency}
                                loadingStates={loadingStates}
                                error={error}
                            />
                        </MetricsContainer>
                    </TabsContent>

                    <TabsContent value="subscription">
                        <MetricsContainer>
                            <SubscriberMetricsV4
                                data={analyticsData as any}
                                granularity={granularity}
                                loadingStates={loadingStates}
                                error={error}
                            />
                        </MetricsContainer>
                    </TabsContent>

                    <TabsContent value="approval-rate">
                        <MetricsContainer>
                            <ApprovalRatesMetricsV4
                                data={(analyticsData as any)?.approvalRatesByBillingCycle}
                                isLoading={loadingStates?.approvalRates ?? false}
                                error={error}
                            />
                        </MetricsContainer>
                    </TabsContent>

                    <TabsContent value="upsell-take-rate">
                        <MetricsContainer>
                            <UpsellTakeRateMetrics
                                data={analyticsData as any}
                                granularity={granularity}
                                loadingStates={{
                                    upsellTakeRate: loadingStates?.upsellTakeRate ?? false,
                                }}
                                error={error}
                            />
                        </MetricsContainer>
                    </TabsContent>

                    <TabsContent value="calendar">
                        <MetricsContainer>
                            {searchParams.get('date') ? (
                                <SubscriptionDetailView date={searchParams.get('date')!} />
                            ) : (
                                <div className="h-auto min-h-[640px] lg:h-[800px]">
                                    <SubscriptionCalendar />
                                </div>
                            )}
                        </MetricsContainer>
                    </TabsContent>

                    <TabsContent value="conversion">
                        <div className="mt-6 space-y-8">
                            {/* Section: Customer Journey */}
                            <div>
                                <h2 className="text-lg font-semibold text-foreground mb-4 border-b border-border pb-2">
                                    {t('page.customerJourney')}
                                </h2>
                                <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-950/20">
                                    <p className="text-sm text-blue-700 dark:text-blue-300">
                                        <Trans i18nKey="page.customerJourneyNote" ns="analytics" components={{ strong: <strong /> }} />
                                    </p>
                                </div>
                                <CustomerConversionFunnel />
                            </div>

                            {/* Section: Conversion Timing Analytics */}
                            <div>
                                <h2 className="text-lg font-semibold text-foreground mb-4 border-b border-border pb-2">
                                    {t('page.conversionTimingAnalytics')}
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <CheckoutToOrderPaidTime />
                                    <CheckoutToEmailEnteredTime />
                                    <CheckoutToBillingAddressTime />
                                </div>
                            </div>

                            {/* Section: Conversion Over Time */}
                            <div>
                                <h2 className="text-lg font-semibold text-foreground mb-4 border-b border-border pb-2">
                                    {t('page.conversionOverTime')}
                                </h2>
                                <ConversionOverTime />
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="product">
                        <MetricsContainer>
                            <ProductMetricsV4
                                data={analyticsData as any}
                                granularity={granularity}
                                currency={selectedCurrency}
                                loadingStates={{
                                    productsByRevenue: loadingStates?.productsByRevenue ?? false,
                                    variantsByRevenue: loadingStates?.variantsByRevenue ?? false,
                                    topSellingProducts: loadingStates?.topSellingProducts ?? false,
                                    productSalesOverTime: loadingStates?.productSalesOverTime ?? false,
                                }}
                                error={error}
                            />
                        </MetricsContainer>
                    </TabsContent>
                </Tabs>
            </div>
        </section>
    );
}

function AnalyticsPage() {
    return (
        <StatsFiltersProvider>
            <AnalyticsV4Content />
        </StatsFiltersProvider>
    );
}

export default withRequiredPermission(AnalyticsPage, PERMISSIONS.ANALYTICS_VIEW);


